/**
 * How a local room edit becomes a write — the planner behind Step B of the
 * element-store plan, and what closes #2716.
 *
 * The room store writes only the FIELDS an edit changed (`utils/roomDocDiff`,
 * since #1741). That was still a whole-array write per field: a local edit to
 * `objects` sent the store's whole `objects` array, so an object a cloud tool
 * deleted a snapshot ago came back with it. This turns each changed array
 * field into OPERATIONS by id — `upsert` the elements that changed, `add`
 * the ones that appeared, `remove` the ids that went — which the adapter applies in a
 * transaction against what the server holds. An element deleted elsewhere is
 * neither re-sent nor re-created; last write wins per ELEMENT, which is the
 * right unit.
 *
 * Three cases keep the whole-field write, deliberately:
 *   - an array whose elements do not all carry a string `id` (`sections` has
 *     one such element in the corpus) — nothing to address an op to;
 *   - a field with no previous value to diff against (a new room, or a field
 *     the store did not hold) — no basis for a removal list, and a whole
 *     write is what "no prior version" has always meant here;
 *   - an array whose SURVIVING ids changed order — ops by id cannot move an
 *     element, and the order of walls and floors is read positionally in
 *     places, so a reorder is written whole rather than silently dropped.
 *
 * Pure. No Firebase, no React.
 */
import type { ArrayOps } from '../types/structure.types';
import type { RoomOpsByField, RoomWrite } from '../types/room.types';
import { sameStoredValue } from '@shared-lib/rooms/sameStoredValue';

type Elem = { id: string } & Record<string, unknown>;

/** True when `value` is an array every element of which carries a string id. */
export const isIdArray = (value: unknown): value is Elem[] =>
  Array.isArray(value) &&
  value.every(
    (el) =>
      el !== null &&
      typeof el === 'object' &&
      typeof (el as { id?: unknown }).id === 'string' &&
      (el as { id: string }).id.length > 0
  );

/**
 * Same element content. Not plain `JSON.stringify` equality: Firestore hands
 * an element back with its keys in a different order, and an untouched
 * element would be written again as an upsert (`sameStoredValue`).
 */
const sameElement = (a: unknown, b: unknown): boolean => sameStoredValue(a, b);

/**
 * The operations that take `prev` to `next`, by id. `null` when the arrays
 * cannot be expressed as operations (see the three cases above), so the
 * caller writes the field whole. An empty op set means nothing changed.
 */
export function arrayFieldOps(prev: unknown, next: unknown): ArrayOps | null {
  if (!isIdArray(prev) || !isIdArray(next)) return null;
  const prevById = new Map(prev.map((el) => [el.id, el]));
  const nextIds = new Set(next.map((el) => el.id));

  // A reorder of the surviving ids is not expressible by id.
  const prevSurvivors = prev
    .filter((el) => nextIds.has(el.id))
    .map((el) => el.id);
  const nextSurvivors = next
    .filter((el) => prevById.has(el.id))
    .map((el) => el.id);
  if (prevSurvivors.some((id, i) => nextSurvivors[i] !== id)) return null;

  // `upsert` is for an element the store HELD and changed; one it did not
  // hold is an `add`. The distinction matters at the server: an upsert of an
  // id the server no longer has is SKIPPED (deleted elsewhere, never
  // re-created), and an add of a fresh id is appended — so a new object must
  // travel as an add or it would be refused as a resurrection.
  const upsert = next.filter((el) => {
    const was = prevById.get(el.id);
    return was !== undefined && !sameElement(was, el);
  });
  const add = next.filter((el) => !prevById.has(el.id));
  const remove = prev.filter((el) => !nextIds.has(el.id)).map((el) => el.id);
  const ops: ArrayOps = {};
  if (upsert.length) ops.upsert = upsert;
  if (add.length) ops.add = add;
  if (remove.length) ops.remove = remove;
  return ops;
}

export const isEmptyWrite = (write: RoomWrite): boolean =>
  Object.keys(write.ops).length === 0 && Object.keys(write.patch).length === 0;

/**
 * Plan the write for one room: `fields` is the set the store's diff found
 * changed (`null` = no prior version, write the whole body), `prev` the room
 * as the store held it before the edit, `next` as it holds it now.
 */
export function planRoomWrite(
  prev: Record<string, unknown> | null | undefined,
  next: Record<string, unknown>,
  fields: string[] | null
): RoomWrite {
  const ops: RoomOpsByField = {};
  const patch: Record<string, unknown> = {};
  if (fields === null || !prev) {
    for (const [key, value] of Object.entries(next)) {
      if (key !== 'id') patch[key] = value;
    }
    return { ops, patch };
  }
  for (const field of fields) {
    if (field === 'id') continue;
    const fieldOps = arrayFieldOps(prev[field], next[field]);
    if (fieldOps === null) {
      patch[field] = next[field];
      continue;
    }
    if (Object.keys(fieldOps).length > 0) ops[field] = fieldOps;
  }
  return { ops, patch };
}
